/**
 * Resumo mensal em PDF (Financeiro) — funciona no NW e no APK.
 */

import { jsPDF } from 'jspdf';
import {
  calcularFaturamento,
  fmtBrl,
  inicioFimMesISO,
  metodoLabel,
  somarDespesas,
} from './barbeariaLogica';
import type { BarbeariaEstado, MetodoPagamento } from './barbeariaTipos';

function mesLabelPt(ano: number, mes0: number): string {
  const nome = new Date(ano, mes0, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
  return nome.charAt(0).toUpperCase() + nome.slice(1);
}

/** Gera e baixa o PDF do mês (atual ou referência). */
export function baixarResumoMensalPdf(
  estado: BarbeariaEstado,
  dataRef: Date = new Date()
): { ok: true; nomeArquivo: string } | { ok: false; erro: string } {
  try {
    const { ini, fim } = inicioFimMesISO(dataRef);
    const fat = calcularFaturamento(estado, ini, fim);
    const saidas = somarDespesas(estado.despesas, ini, fim);
    const resultado = fat.casa - saidas.total;
    const tituloMes = mesLabelPt(dataRef.getFullYear(), dataRef.getMonth());
    const loja = estado.config.nomeLoja || 'Carioca Barbershop';

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const margem = 16;
    let y = 18;
    const linha = (txt: string, opts?: { bold?: boolean; size?: number; color?: number[] }) => {
      doc.setFont('helvetica', opts?.bold ? 'bold' : 'normal');
      doc.setFontSize(opts?.size ?? 11);
      if (opts?.color) doc.setTextColor(opts.color[0], opts.color[1], opts.color[2]);
      else doc.setTextColor(30, 30, 30);
      doc.text(txt, margem, y);
      y += (opts?.size ?? 11) * 0.45 + 3;
    };

    linha(loja, { bold: true, size: 16 });
    linha(`Resumo financeiro — ${tituloMes}`, { bold: true, size: 13 });
    linha(`Período: ${ini.split('-').reverse().join('/')} a ${fim.split('-').reverse().join('/')}`, {
      size: 10,
      color: [90, 90, 90],
    });
    linha(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, { size: 9, color: [120, 120, 120] });
    y += 4;

    linha('Totais', { bold: true, size: 12 });
    linha(`Faturamento bruto: ${fmtBrl(fat.bruto)}`);
    linha(`Comissões: ${fmtBrl(fat.comissoes)}`);
    linha(`Casa (bruto − comissões): ${fmtBrl(fat.casa)}`);
    linha(`Atendimentos concluídos: ${fat.qtdAtendimentos}`);
    linha(`Despesas (fixas + variáveis): ${fmtBrl(saidas.total)}`);
    linha(`  Fixas: ${fmtBrl(saidas.fixo)} · Variáveis: ${fmtBrl(saidas.variavel)}`, {
      size: 10,
      color: [80, 80, 80],
    });
    linha(`Resultado aproximado (casa − despesas): ${fmtBrl(resultado)}`, { bold: true });
    y += 4;

    linha('Por método de pagamento', { bold: true, size: 12 });
    (Object.keys(fat.porMetodo) as MetodoPagamento[]).forEach((m) => {
      linha(`${metodoLabel(m)}: ${fmtBrl(fat.porMetodo[m])}`, { size: 10 });
    });
    y += 4;

    linha('Por barbeiro', { bold: true, size: 12 });
    if (fat.porBarbeiro.length === 0) {
      linha('Nenhum atendimento concluído neste mês.', { size: 10, color: [100, 100, 100] });
    } else {
      fat.porBarbeiro.forEach((b) => {
        linha(
          `${b.nome}: bruto ${fmtBrl(b.bruto)} · comissão ${fmtBrl(b.comissao)}`,
          { size: 10 }
        );
      });
    }

    y += 8;
    linha('Observação: valores de atendimentos só contam quando o status está “concluído”.', {
      size: 8,
      color: [110, 110, 110],
    });
    linha('Cópia local do aparelho — use este PDF como backup do mês.', {
      size: 8,
      color: [110, 110, 110],
    });

    const nomeArquivo = `Carioca-resumo-${ini.slice(0, 7)}.pdf`;
    doc.save(nomeArquivo);
    return { ok: true, nomeArquivo };
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Falha ao gerar PDF';
    return { ok: false, erro: msg };
  }
}
